/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalFooter,
  Text,
  Space,
  Box,
  SearchInput,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableScrollbar,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  useColorStyle,
  useColorMode,
} from '@tonic-one/react';
import TargetList from '@Components/TargetList';
import {
  PolicyListModalName,
  PolicyListModalState,
  SkippedUserInfo,
} from '@Policy/utils/types';
import React, { useEffect, useMemo, useState } from 'react';
import { PolicyApi } from '@Apis';
import { ManualScanCancelAndRescanInfo } from '@Policy/OverAllPolicyList/Modal/scan';
import { useTranslation } from 'react-i18next';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import SmartTooltip from '@Report/SmartTooltip';
import { char2entity } from '@Utils/common';
import CSpinner from '@Components/CSpinner';

interface Props {
  modal: PolicyListModalState;
  setModal: ({ name, policy, scanHistory }: PolicyListModalState) => void;
  setRefreshTableTrigger: (trigger: Date) => void;
}

interface SkippedUserModalProps {
  title: string;
  modal: PolicyListModalState;
  setModal: ({ name, policy, scanHistory }: PolicyListModalState) => void;
  setRefreshTableTrigger: (trigger: Date) => void;
}

interface SkippedUsersDetails {
  title: string;
  subDesc?: string;
  isInvalidShow?: boolean;
  usersList: SkippedUserInfo[];
}

const CheckSkippedModalWithLessInfo = ({
  title,
  modal,
  setModal,
  setRefreshTableTrigger,
}: SkippedUserModalProps) => {
  if (modal.name !== PolicyListModalName.CheckSkipped) {
    return <></>;
  }
  const { t } = useTranslation();
  const [failedUserListKeyMap, setFailedUserListKeyMap] = useState<
    { title: string }[]
  >([]);
  const onClose = () => {
    setModal({
      name: PolicyListModalName.None,
      policy: null,
    });
  };
  const onRescan = async () => {
    try {
      const cancelManualScanInfo: ManualScanCancelAndRescanInfo = {
        policyType: modal.policy.policyType,
      };
      await PolicyApi.rescanManualScan(
        modal.scanHistory['taskId'],
        cancelManualScanInfo
      );
    } catch (error) {
      console.log('error', JSON.stringify(error));
    } finally {
      onClose();
      setRefreshTableTrigger(new Date());
    }
  };

  const subTitle = () => {
    switch (modal.policy?.policyType) {
      case 1:
        return t('policy.skippedUser.mailboxes');
      case 2:
        return t('policy.skippedUser.sites');
      case 41:
        return t('policy.skippedUser.teams');
      default:
        return t('policy.skippedUser.users');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);
  const fetchData = async () => {
    const failedUserList = (
      await PolicyApi.getFailedUserList(
        modal.scanHistory['taskId'],
        modal.policy.policyType
      )
    ).data;
    const failedUserListKeyMap = failedUserList.map((user) => ({
      title: user,
    }));
    setFailedUserListKeyMap(failedUserListKeyMap);
  };
  return (
    <Modal
      ensureFocus={true}
      autoFocus={false}
      closeOnEsc={true}
      closeOnOutsideClick={false}
      isClosable={true}
      isOpen={modal.name === PolicyListModalName.CheckSkipped}
      onClose={onClose}
      size="xl"
    >
      <ModalOverlay opacity={0.9} />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalBody>
          <TargetList
            title={subTitle()}
            disabled={false}
            list={failedUserListKeyMap}
            limit={500}
            // width="520px"
            size={9}
          />
          <Text>{t('policy.checkSkippedUser.desc')}</Text>
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={onRescan}>
            {t('policy.button.rescan')}
          </Button>
          <Space width="2x" />
          <Button minWidth="20x" onClick={onClose}>
            {t('common.button.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const CheckSkippedModalWithMoreInfo = ({
  title,
  modal,
  setModal,
  setRefreshTableTrigger,
}: SkippedUserModalProps) => {
  if (modal.name !== PolicyListModalName.CheckSkipped) {
    return <></>;
  }
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const [skippedUsersInfo, setSkippedUsersInfo] = useState<SkippedUserInfo[]>(
    []
  );
  const [invalidSkippedUsersInfo, setInvalidSkippedUsersInfo] = useState<
    SkippedUserInfo[]
  >([]);
  const [otherSkippedUsersInfo, setOtherSkippedUsersInfo] = useState<
    SkippedUserInfo[]
  >([]);
  const onClose = () => {
    setModal({
      name: PolicyListModalName.None,
      policy: null,
    });
  };
  const onRescan = async () => {
    try {
      const cancelManualScanInfo: ManualScanCancelAndRescanInfo = {
        policyType: modal.policy.policyType,
      };
      await PolicyApi.rescanManualScan(
        modal.scanHistory['taskId'],
        cancelManualScanInfo
      );
    } catch (error) {
      console.log('error', JSON.stringify(error));
    } finally {
      onClose();
      setRefreshTableTrigger(new Date());
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const skippedUsersList = (
        await PolicyApi.getSkippedUsersList(
          modal.scanHistory['taskId'],
          modal.policy.policyType
        )
      ).data;
      skippedUsersList.invalidUsers.forEach((user) => {
        user.status = 'invalid';
      });
      skippedUsersList.otherUsers.forEach((user) => {
        user.status = 'other';
      });
      skippedUsersList.absentUsers.forEach((user) => {
        user.status = 'absent';
      });
      setSkippedUsersInfo(skippedUsersList.failedUsers);
      setInvalidSkippedUsersInfo(skippedUsersList.invalidUsers);
      setOtherSkippedUsersInfo([
        ...skippedUsersList.otherUsers,
        ...skippedUsersList.absentUsers,
      ]);
    } catch (error) {
      console.log('error', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <CSpinner />}
      <Modal
        ensureFocus={true}
        autoFocus={false}
        closeOnEsc={true}
        closeOnOutsideClick={false}
        isClosable={true}
        isOpen={modal.name === PolicyListModalName.CheckSkipped}
        onClose={onClose}
        size="lg"
      >
        <ModalOverlay opacity={0.9} />
        <ModalContent>
          <ModalHeader>{title}</ModalHeader>
          <ModalBody>
            <SkippedUsersModal
              title={t('policy.skippedUsersList.retry.mailboxes.title')}
              usersList={skippedUsersInfo}
            />
            <Button
              mt="20px"
              variant="primary"
              onClick={onRescan}
              disabled={skippedUsersInfo.length === 0}
            >
              {t('policy.button.rescan')}
            </Button>
            <Space width="2x" />
            <Divider
              mt="16px"
              mb="16px"
              variant="solid"
              orientation="horizontal"
            />
            <SkippedUsersModal
              isInvalidShow={true}
              title={t('policy.skippedUsersList.invalid.mailboxes.title')}
              usersList={[...invalidSkippedUsersInfo, ...otherSkippedUsersInfo]}
            />
          </ModalBody>
          <ModalFooter>
            <Button minWidth="20x" onClick={onClose}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const SkippedUsersModal = ({
  title,
  subDesc = '',
  isInvalidShow = false,
  usersList,
}: SkippedUsersDetails) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const [allData, setAllData] = useState<SkippedUserInfo[]>([]);
  const [keyword, setKeyword] = useState('');
  const filteredData = useMemo(() => {
    const lowerCaseKeyword = keyword.toLowerCase();
    return keyword === ''
      ? allData
      : allData.filter(({ id, displayname }) => {
          return (
            id.toLowerCase().includes(lowerCaseKeyword) ||
            displayname.toLowerCase().includes(lowerCaseKeyword)
          );
        });
  }, [keyword, allData]);

  useEffect(() => {
    setAllData(usersList);
  }, [usersList]);

  const formatReason = (status: string) => {
    if (status === 'invalid') {
      return t('policy.skippedUsersList.invalid.mailboxes.reason.invalid');
    } else if (status === 'other' || status === 'absent') {
      return t('policy.skippedUsersList.invalid.mailboxes.reason.other');
    } else {
      return '';
    }
  };
  const columns: ColumnDef<SkippedUserInfo>[] = useMemo(
    () => [
      {
        id: 'displayname',
        accessorKey: 'displayname',
        header: () => t('policy.skippedUsersList.columns.username'),
        cell: ({ row }) => {
          const { displayname } = row.original;
          return <SmartTooltip>{char2entity(displayname)}</SmartTooltip>;
        },
        meta: {
          customColumnStyle: {
            minWidth: 40,
            width: isInvalidShow ? '24%' : '58%',
          },
        },
      },
      {
        id: 'id',
        accessorKey: 'id',
        header: () => t('policy.skippedUsersList.columns.emailAddress'),
        meta: {
          customColumnStyle: {
            minWidth: 40,
            width: isInvalidShow ? '38%' : '52%',
          },
        },
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: () => {
          if (isInvalidShow) {
            return t('policy.skippedUsersList.columns.invalid');
          } else {
            return null;
          }
        },
        cell: ({ row }) => {
          const { status } = row.original;
          return (
            <>
              {isInvalidShow ? (
                <SmartTooltip>{formatReason(status)}</SmartTooltip>
              ) : (
                <></>
              )}
            </>
          );
        },
        meta: {
          customColumnStyle: {
            minWidth: 40,
            width: '38%',
          },
        },
      },
    ],
    [allData, filteredData, usersList]
  );

  const table = useReactTable({
    data: filteredData,
    columns,
    defaultColumn: {
      minSize: 40,
    },
    getRowId: (row) => `${row.id}`,
    getCoreRowModel: getCoreRowModel(),
  });
  return (
    <Box>
      <Text
        mt="16px"
        fontSize="lg"
        lineHeight="lg"
        color={colorStyle.color.primary}
      >
        {title} ({usersList.length})
      </Text>
      {subDesc && (
        <Text mt="16px" color={colorStyle.color.secondary}>
          {subDesc}
        </Text>
      )}
      <SearchInput
        mt="16px"
        mb="16px"
        placeholder={t('policy.placeholder.selectedTargets.items')}
        onChange={(e: any) => {
          setKeyword(e.target.value);
        }}
        onClearInput={() => {
          setKeyword('');
        }}
      />
      <Table mt="16px" size="lg" display="flex">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableHeaderRow key={headerGroup.id} width="100%">
              {headerGroup.headers.map((header) => {
                const styleProps = {
                  minWidth: header.column.columnDef.minSize,
                  width: header.getSize(),
                  ...header.column.columnDef.meta?.customColumnStyle,
                };
                return (
                  <TableHeaderCell key={header.id} {...styleProps}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHeaderCell>
                );
              })}
            </TableHeaderRow>
          ))}
        </TableHeader>
        <TableScrollbar
          maxHeight="220px"
          overflow="visible" // Make the scrollbar visible
          overflowX="hidden"
        >
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} width="100%">
                {row.getVisibleCells().map((cell) => {
                  const styleProps = {
                    minWidth: cell.column.columnDef.minSize,
                    width: cell.column.getSize(),
                    ...cell.column.columnDef.meta?.customColumnStyle,
                  };
                  return (
                    <TableCell key={cell.id} {...styleProps}>
                      <SmartTooltip>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </SmartTooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </TableScrollbar>
      </Table>
    </Box>
  );
};

const CheckSkippedModal = ({
  modal,
  setModal,
  setRefreshTableTrigger,
}: Props) => {
  if (modal.name !== PolicyListModalName.CheckSkipped) {
    return <></>;
  }
  const { t } = useTranslation();
  const title = () => {
    switch (modal.policy?.policyType) {
      case 1:
        return t('policy.checkSkippedUser.mailboxes');
      case 2:
        return t('policy.checkSkippedUser.sites');
      case 41:
        return t('policy.checkSkippedUser.teams');
      default:
        return t('policy.checkSkippedUser.users');
    }
  };
  return (
    <>
      {modal.policy.policyType === 1 ? (
        <CheckSkippedModalWithMoreInfo
          title={title()}
          modal={modal}
          setModal={setModal}
          setRefreshTableTrigger={setRefreshTableTrigger}
        />
      ) : (
        <CheckSkippedModalWithLessInfo
          title={title()}
          modal={modal}
          setModal={setModal}
          setRefreshTableTrigger={setRefreshTableTrigger}
        />
      )}
    </>
  );
};

export default CheckSkippedModal;
